import React, { useState } from 'react'
import { Button, Checkbox, Upload, Modal, Descriptions, Space, App } from 'antd'
import { DownloadOutlined, UploadOutlined, ExportOutlined, ImportOutlined } from '@ant-design/icons'
import type { UploadFile } from 'antd'
import { useI18n } from '@/shared/i18n'
import { useExportTenant, useImportTenant } from '@/shared/api/companyApi'
import type { ImportTenantSummary } from '@/shared/api/companyApi'
import { useCurrentCompany } from '@/stores/authStore'

const TenantExportImport: React.FC = () => {
  const { t } = useI18n('modules')
  const { message } = App.useApp()
  const company = useCurrentCompany()
  const exportMutation = useExportTenant()
  const importMutation = useImportTenant()

  const [includeKb, setIncludeKb] = useState(false)
  const [importSummary, setImportSummary] = useState<ImportTenantSummary | null>(null)
  const [showSummary, setShowSummary] = useState(false)

  const handleExport = async () => {
    if (!company?.id) {
      message.error(t('settings.tenantExport.noCompany'))
      return
    }
    try {
      const blob = await exportMutation.mutateAsync({
        companyId: company.id,
        includeKb,
      })
      // 触发浏览器下载
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)
      a.download = `tenant_export_${company.id}_${company.name || ''}_${timestamp}.zip`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      message.success(t('settings.tenantExport.exportSuccess'))
    } catch (e: any) {
      message.error(e?.message || t('settings.tenantExport.exportFailed'))
    }
  }

  const handleImport = async (file: File) => {
    try {
      const result = await importMutation.mutateAsync(file)
      if (result.success) {
        setImportSummary(result.summary)
        setShowSummary(true)
        message.success(t('settings.tenantExport.importSuccess'))
      }
    } catch (e: any) {
      message.error(e?.message || t('settings.tenantExport.importFailed'))
    }
    return false // 阻止 antd 自动上传
  }

  return (
    <div>
      <h3>{t('settings.tenantExport.title')}</h3>

      {/* 导出 */}
      <Space direction="vertical" style={{ width: '100%', marginBottom: 24 }}>
        <Space>
          <Button
            icon={<ExportOutlined />}
            onClick={handleExport}
            loading={exportMutation.isPending}
            type="primary"
          >
            {t('settings.tenantExport.exportButton')}
          </Button>
          <Checkbox
            checked={includeKb}
            onChange={(e) => setIncludeKb(e.target.checked)}
          >
            {t('settings.tenantExport.includeKb')}
          </Checkbox>
        </Space>
      </Space>

      {/* 导入 */}
      <Space direction="vertical" style={{ width: '100%' }}>
        <Upload
          accept=".zip"
          showUploadList={false}
          beforeUpload={(file) => {
            handleImport(file)
            return false
          }}
        >
          <Button
            icon={<ImportOutlined />}
            loading={importMutation.isPending}
          >
            {t('settings.tenantExport.importButton')}
          </Button>
        </Upload>
      </Space>

      {/* 导入结果摘要 Modal */}
      <Modal
        title={t('settings.tenantExport.importSummaryTitle')}
        open={showSummary}
        onOk={() => setShowSummary(false)}
        onCancel={() => setShowSummary(false)}
        footer={[
          <Button key="ok" type="primary" onClick={() => setShowSummary(false)}>
            {t('settings.tenantExport.confirm')}
          </Button>,
        ]}
      >
        {importSummary && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label={t('settings.tenantExport.summary.company')}>
              {importSummary.company}
            </Descriptions.Item>
            <Descriptions.Item label={t('settings.tenantExport.summary.settings')}>
              {importSummary.settings}
            </Descriptions.Item>
            <Descriptions.Item label={t('settings.tenantExport.summary.connectors')}>
              {t('settings.tenantExport.summary.created')}: {importSummary.connectors.created},
              {' '}{t('settings.tenantExport.summary.updated')}: {importSummary.connectors.updated}
            </Descriptions.Item>
            <Descriptions.Item label={t('settings.tenantExport.summary.dataSources')}>
              {t('settings.tenantExport.summary.created')}: {importSummary.data_sources.created},
              {' '}{t('settings.tenantExport.summary.updated')}: {importSummary.data_sources.updated}
            </Descriptions.Item>
            <Descriptions.Item label={t('settings.tenantExport.summary.platformIntegrations')}>
              {t('settings.tenantExport.summary.created')}: {importSummary.platform_integrations.created},
              {' '}{t('settings.tenantExport.summary.updated')}: {importSummary.platform_integrations.updated}
            </Descriptions.Item>
            <Descriptions.Item label={t('settings.tenantExport.summary.users')}>
              {t('settings.tenantExport.summary.created')}: {importSummary.users.created},
              {' '}{t('settings.tenantExport.summary.skipped')}: {importSummary.users.skipped}
            </Descriptions.Item>
            <Descriptions.Item label={t('settings.tenantExport.summary.knowledgeBase')}>
              {t('settings.tenantExport.summary.imported')}: {importSummary.knowledge_base.imported}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  )
}

export default TenantExportImport
